import React, { useState } from 'react';
import axios from 'axios';
import { Shield, Eye, Brain, CheckCircle, AlertTriangle, ArrowRight, RefreshCcw, Lock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const API_BASE = "/api/v1/scam-sense";

// --- GMI free-tier helpers ---
const getClientId = () => {
    let cid = localStorage.getItem("ss_client_id");
    if (!cid) {
        cid = `ss-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem("ss_client_id", cid);
    }
    return cid;
};
const getUserGmiKey = () => localStorage.getItem("ss_gmi_key") || "";
const setUserGmiKey = (k) => k.trim() ? localStorage.setItem("ss_gmi_key", k.trim()) : localStorage.removeItem("ss_gmi_key");
const gmiHeaders = () => {
    const key = getUserGmiKey();
    return key ? { "x-user-gmi-key": key } : { "x-client-id": getClientId() };
};

// Renders the playbook lesson as readable points. The server strips markdown and
// leaves one point per line; a leading "Label:" becomes a bolded lead-in so the
// structure survives without shipping a markdown renderer.
const PlaybookLesson = ({ text }) => {
    const lines = String(text || '')
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean);

    if (lines.length === 0) return null;
    if (lines.length === 1) {
        return <p className="text-lg leading-relaxed text-slate-700">{lines[0]}</p>;
    }

    // First line is usually a title rather than a point.
    const [lead, ...points] = lines;
    return (
        <div className="space-y-4">
            <p className="text-lg font-semibold text-slate-800">{lead}</p>
            <ul className="space-y-3">
                {points.map((line, i) => {
                    const m = line.match(/^([^:]{3,60}):\s*(.+)$/);
                    return (
                        <li key={i} className="flex gap-3 text-lg leading-relaxed text-slate-700">
                            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-blue-600 shrink-0" />
                            <span>
                                {m ? (
                                    <>
                                        <span className="font-semibold text-slate-900">{m[1]}: </span>
                                        {m[2]}
                                    </>
                                ) : (
                                    line
                                )}
                            </span>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

const GmiCheck = () => (
    <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
        <path fill="#1d9bf0" d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91s-2.52-1.27-3.91-.81c-.66-1.31-1.91-2.19-3.34-2.19s-2.67.88-3.33 2.19c-1.4-.46-2.91-.2-3.92.81s-1.26 2.52-.8 3.91c-1.31.67-2.2 1.91-2.2 3.34s.89 2.67 2.2 3.34c-.46 1.39-.21 2.9.8 3.91s2.52 1.26 3.91.81c.67 1.31 1.91 2.19 3.34 2.19s2.68-.88 3.34-2.19c1.39.45 2.9.2 3.91-.81s1.27-2.52.81-3.91c1.31-.67 2.19-1.91 2.19-3.34zm-11.71 4.2L6.8 12.46l1.41-1.42 2.26 2.26 4.8-5.23 1.47 1.36-6.2 6.77z"/>
    </svg>
);

const ScamSenseApp = () => {
    const [step, setStep] = useState('UPLOAD'); // UPLOAD, SCANNING, QUIZ, RESULT
    const [imageUrl, setImageUrl] = useState('');
    const [file, setFile] = useState(null);
    const [errorMsg, setErrorMsg] = useState('');
    const [textContent, setTextContent] = useState('');
    const [backendData, setBackendData] = useState(null);
    const [selectedOption, setSelectedOption] = useState(null);
    const [reporting, setReporting] = useState(false);
    const [reportSent, setReportSent] = useState(false);
    const [keyModalOpen, setKeyModalOpen] = useState(false);
    const [keyInput, setKeyInput] = useState('');
    const [hasKey, setHasKey] = useState(!!getUserGmiKey());
    const [freeLeft, setFreeLeft] = useState(null);
    const [models, setModels] = useState(null);
    const [elapsed, setElapsed] = useState(0);

    const refreshQuota = () => {
        fetch(`/api/quota?cid=${encodeURIComponent(getClientId())}`)
            .then(r => r.json())
            .then(q => setFreeLeft(q.remaining))
            .catch(() => {});
    };
    React.useEffect(refreshQuota, []);

    // Model names come from the server so this screen can never claim a model the
    // backend is not actually using.
    React.useEffect(() => {
        fetch('/api/models').then(r => r.json()).then(setModels).catch(() => {});
    }, []);

    // A scan takes ~1 minute; show elapsed seconds so the wait reads as progress
    // rather than a hang.
    React.useEffect(() => {
        if (step !== 'SCANNING') { setElapsed(0); return; }
        const started = Date.now();
        const id = setInterval(() => setElapsed(Math.round((Date.now() - started) / 1000)), 1000);
        return () => clearInterval(id);
    }, [step]);

    const shortModel = (slug) => (slug ? String(slug).split('/').pop() : null);
    
    // --- Actions ---
    const handleScan = async () => {
        if (!file && !imageUrl.trim()) {
            setErrorMsg('Choose an image file, or paste a direct image URL.');
            return;
        }
        setErrorMsg('');
        setStep('SCANNING');

        try {
            let response;
            if (file) {
                // Multipart upload. The server reads the file straight off disk, so
                // this path works even for images no public URL can serve.
                const fd = new FormData();
                fd.append('images', file);
                fd.append('userId', 'user_demo_123');
                if (textContent) fd.append('text_content', textContent);
                response = await axios.post(`${API_BASE}/scan`, fd, { headers: gmiHeaders() });
            } else {
                response = await axios.post(`${API_BASE}/scan`, {
                    userId: 'user_demo_123',
                    image_url: imageUrl.trim(),
                    text_content: textContent
                }, { headers: gmiHeaders() });
            }
            setBackendData(response.data);
            refreshQuota();
            setStep('QUIZ');
        } catch (err) {
            setStep('UPLOAD');
            const status = err.response && err.response.status;
            if (status === 402) {
                refreshQuota();
                setKeyModalOpen(true);
                return;
            }
            // Show the real reason. The old catch-all blamed the backend for every
            // failure, including plain bad-image-URL errors.
            const data = err.response && err.response.data;
            const serverMsg = data && (data.error || data.message);
            if (serverMsg) {
                setErrorMsg(serverMsg);
            } else if (err.request && !err.response) {
                setErrorMsg('Could not reach the server. Check your connection and try again.');
            } else {
                setErrorMsg(err.message || 'Something went wrong. Please try again.');
            }
        }
    };

    const handleAnswer = (option) => {
        setSelectedOption(option);
        setStep('RESULT');
    };
    
    const handleReport = async () => {
        setReporting(true);
        try {
            await axios.post(`${API_BASE}/report`, {
                visualDNA: backendData.visualDNA,
                gameData: backendData.gameData,
                userEmail: "user@example.com" // You might want to add email input field
            }, { headers: gmiHeaders() });
            setReportSent(true);
            refreshQuota();
        } catch (err) {
            if (err.response && err.response.status === 402) {
                refreshQuota();
                setKeyModalOpen(true);
            } else {
                alert("Report failed to send.");
            }
        }
        setReporting(false);
    };
    
    const reset = () => {
        setStep('UPLOAD');
        setImageUrl('');
        setFile(null);
        setErrorMsg('');
        setTextContent('');
        setBackendData(null);
        setSelectedOption(null);
        setReportSent(false);
    };

    return (
        <div className="min-h-screen bg-slate-50 text-slate-900 font-sans p-4 md:p-8">
            <div className="max-w-2xl mx-auto">
                {/* Header */}
                <header className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-2">
                        <Shield className="text-blue-600 w-8 h-8" />
                        <h1 className="text-2xl font-bold tracking-tight">SCAM-SENSE</h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <a href="https://console.gmicloud.ai" target="_blank" rel="noopener noreferrer"
                           className="flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 px-3 py-1 rounded-full text-sm font-semibold hover:border-blue-400">
                            Powered by GMI <GmiCheck />
                        </a>
                        <button onClick={() => { refreshQuota(); setKeyModalOpen(true); }}
                                className={`px-3 py-1 rounded-full text-sm font-medium border ${hasKey ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-slate-200 text-slate-600'}`}>
                            {hasKey ? 'API key set ✓' : (freeLeft !== null ? `API key · ${freeLeft}/3 free` : 'API key')}
                        </button>
                        <div className="hidden sm:flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                            <Lock className="w-4 h-4" /> Shield Level 1
                        </div>
                    </div>
                </header>

                {/* GMI API key modal */}
                {keyModalOpen && (
                    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setKeyModalOpen(false)}>
                        <div className="bg-white rounded-3xl shadow-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                            <h3 className="text-xl font-bold mb-1">GMI API Key</h3>
                            <p className="text-sm text-slate-500 mb-1">
                                {!hasKey && freeLeft !== null
                                    ? `You have ${freeLeft} of 3 free scans left. Add your own GMI key for unlimited scans.`
                                    : 'Your key is stored only in this browser and sent per request.'}
                            </p>
                            <a href="https://console.gmicloud.ai/user-setting/api-keys" target="_blank" rel="noopener noreferrer"
                               className="inline-block text-sm text-blue-600 font-medium mb-4 hover:underline">
                                Get your API key →
                            </a>
                            <input type="password" value={keyInput} onChange={(e) => setKeyInput(e.target.value)}
                                   placeholder={hasKey ? 'Key saved — paste a new one to replace' : 'gmi-...'}
                                   className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 mb-4 outline-none focus:border-blue-400" />
                            <div className="flex items-center justify-between gap-2">
                                {hasKey ? (
                                    <button onClick={() => { setUserGmiKey(''); setHasKey(false); }}
                                            className="text-sm text-red-500 font-medium">Remove key</button>
                                ) : <span />}
                                <div className="flex gap-2">
                                    <button onClick={() => setKeyModalOpen(false)}
                                            className="px-4 py-2 rounded-2xl border border-slate-200 text-slate-600 font-medium">Cancel</button>
                                    <button onClick={() => { setUserGmiKey(keyInput); setHasKey(!!keyInput.trim()); setKeyInput(''); setKeyModalOpen(false); }}
                                            disabled={!keyInput.trim()}
                                            className="px-5 py-2 rounded-2xl bg-blue-600 text-white font-bold disabled:opacity-50">Save</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <AnimatePresence mode="wait">
                    {/* STEP 1: UPLOAD */}
                    {step === 'UPLOAD' && (
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
                            className="bg-white p-8 rounded-3xl shadow-xl border border-slate-100"
                        >
                            <h2 className="text-3xl font-bold mb-2">Is this a Mind Game?</h2>
                            <p className="text-slate-500 mb-8">Upload a screenshot of the suspicious message, or paste a link to one.</p>

                            <div className="space-y-4">
                                <label className="block bg-slate-50 p-5 rounded-2xl border-2 border-dashed border-slate-200 cursor-pointer hover:border-blue-400 transition-colors text-center">
                                    <input
                                        type="file"
                                        accept="image/png,image/jpeg,image/gif,image/webp"
                                        className="hidden"
                                        onChange={(e) => { setFile(e.target.files[0] || null); setErrorMsg(''); }}
                                    />
                                    {file ? (
                                        <span className="text-lg font-medium text-blue-700">📎 {file.name}</span>
                                    ) : (
                                        <span className="text-lg text-slate-600">Click to upload a screenshot <span className="text-slate-400">(PNG, JPG, GIF, WEBP · max 10MB)</span></span>
                                    )}
                                </label>

                                {file ? (
                                    <button onClick={() => setFile(null)} className="text-sm text-slate-500 hover:text-red-500 font-medium">
                                        Remove file
                                    </button>
                                ) : (
                                    <>
                                        <div className="flex items-center gap-3 text-xs text-slate-400 uppercase tracking-widest">
                                            <span className="flex-1 h-px bg-slate-200" /> or <span className="flex-1 h-px bg-slate-200" />
                                        </div>
                                        <div className="bg-slate-50 p-4 rounded-2xl border-2 border-dashed border-slate-200">
                                            <input
                                                type="text"
                                                placeholder="Paste a direct image URL (jpg/png)..."
                                                className="w-full bg-transparent outline-none text-lg"
                                                value={imageUrl}
                                                onChange={(e) => { setImageUrl(e.target.value); setErrorMsg(''); }}
                                            />
                                        </div>
                                    </>
                                )}

                                {errorMsg && (
                                    <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl px-4 py-3 text-sm">
                                        {errorMsg}
                                    </div>
                                )}
                                <textarea 
                                    placeholder="Optional: What did the message say?"
                                    className="w-full bg-slate-50 p-4 rounded-2xl outline-none border border-slate-100 h-32"
                                    value={textContent}
                                    onChange={(e) => setTextContent(e.target.value)}
                                />
                                <button 
                                    onClick={handleScan}
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-2xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-200"
                                >
                                    Scan for Mind Games <ArrowRight />
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {/* STEP 2: SCANNING */}
                    {step === 'SCANNING' && (
                        <motion.div 
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="text-center py-20"
                        >
                            <div className="relative inline-block mb-8">
                                <motion.div 
                                    animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 2 }}
                                    className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center"
                                >
                                    <Eye className="w-12 h-12 text-blue-600" />
                                </motion.div>
                                <motion.div 
                                    animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 4, ease: "linear" }}
                                    className="absolute inset-0 border-4 border-t-blue-600 border-transparent rounded-full"
                                />
                            </div>
                            <h2 className="text-2xl font-bold mb-2">Digital Eyes scanning Visual DNA...</h2>
                            <p className="text-slate-500">
                                {models
                                    ? `Vision: ${shortModel(models.vision)} · Analysis: ${shortModel(models.text)} on GMI Cloud`
                                    : 'Consulting the Virtual Expert Team on GMI Cloud...'}
                            </p>
                            <p className="mt-4 text-sm text-slate-400">
                                This usually takes about a minute — hang tight.
                            </p>
                            <p className="mt-1 text-sm font-mono text-blue-600">
                                {elapsed}s elapsed
                            </p>
                        </motion.div>
                    )}

                    {/* STEP 3: QUIZ */}
                    {step === 'QUIZ' && (
                        <motion.div 
                            initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }}
                            className="bg-white p-8 rounded-3xl shadow-xl"
                        >
                            <div className="flex items-center gap-2 text-orange-600 font-bold mb-4 uppercase tracking-widest text-sm">
                                <Brain className="w-5 h-5" /> Mind Game Detected
                            </div>
                            <h2 className="text-2xl font-bold mb-6">{backendData.gameData.question}</h2>
                            <div className="space-y-3">
                                {backendData.gameData.options.map((opt, i) => (
                                    <button 
                                        key={i}
                                        onClick={() => handleAnswer(opt)}
                                        className="w-full text-left p-4 rounded-2xl border-2 border-slate-100 hover:border-blue-500 hover:bg-blue-50 transition-all font-medium"
                                    >
                                        {opt}
                                    </button>
                                ))}
                            </div>
                        </motion.div>
                    )}

                    {/* STEP 4: RESULT */}
                    {step === 'RESULT' && (
                        <motion.div 
                            initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                            className="bg-white rounded-3xl shadow-2xl overflow-hidden"
                        >
                            <div className={`p-8 ${selectedOption === backendData.gameData.correct_trick ? 'bg-green-600' : 'bg-slate-800'} text-white`}>
                                <div className="flex items-center gap-2 mb-4">
                                    <CheckCircle className="w-8 h-8" />
                                    <h2 className="text-3xl font-bold">Shield Level Up!</h2>
                                </div>
                                <p className="text-xl opacity-90">
                                    {selectedOption === backendData.gameData.correct_trick 
                                        ? "You spotted the Scammer's secret weapon!" 
                                        : "Don't worry, even experts get tricked. Here is what happened."}
                                </p>
                            </div>
                            <div className="p-8">
                                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4 font-mono">The Scammer's Playbook</h3>
                                {/* The lesson arrives markdown-stripped, one point per line
                                    (see src/utils/sanitize.js). Render the points as a list
                                    instead of one wall of text. */}
                                <PlaybookLesson text={backendData.gameData.playbook_lesson} />
                                
                                {/* Report Button Section - Added below The Scammer's Playbook */}
                                <div className="mt-8 pt-8 border-t border-slate-100">
                                    {!reportSent ? (
                                        <button 
                                            onClick={handleReport}
                                            disabled={reporting}
                                            className="w-full bg-red-50 hover:bg-red-100 text-red-600 font-bold py-4 rounded-2xl flex items-center justify-center gap-2 transition-all border border-red-200"
                                        >
                                            {reporting ? "Generating Formal Evidence..." : "🚨 Report to Authorities"}
                                        </button>
                                    ) : (
                                        <motion.div 
                                            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                                            className="p-4 bg-green-50 text-green-700 rounded-2xl border border-green-200 flex items-center gap-3 font-bold"
                                        >
                                            <CheckCircle /> Formal Report Filed with Global Shield Network
                                        </motion.div>
                                    )}
                                </div>
                                
                                <div className="mt-8 pt-8 border-t border-slate-100 flex gap-4">
                                    <button 
                                        onClick={reset}
                                        className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-900 font-bold py-4 rounded-2xl flex items-center justify-center gap-2"
                                    >
                                        <RefreshCcw className="w-5 h-5" /> Scan Another
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Footer Visual DNA Badge */}
                {backendData && step === 'RESULT' && (
                    <div className="mt-6 p-4 bg-blue-50 rounded-2xl border border-blue-100 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="bg-blue-600 p-2 rounded-lg text-white">
                                <AlertTriangle className="w-5 h-5" />
                            </div>
                            <div>
                                <p className="text-xs font-bold text-blue-800 uppercase">Nuclear X Analysis</p>
                                <p className="text-sm text-blue-600 font-medium">Visual Red Flags: {backendData.visualDNA?.detected_red_flags?.length || "High"}</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="text-xs font-bold text-blue-800 uppercase">Risk Score</p>
                            <p className="text-xl font-black text-blue-600">{(backendData.visualDNA?.scam_score * 100 || 97)}%</p>
                        </div>
                    </div>
                )}
                {/* GMI footer CTA */}
                <footer className="mt-12 pt-8 border-t border-slate-200 flex flex-col items-center gap-3 pb-6">
                    <a href="https://console.gmicloud.ai" target="_blank" rel="noopener noreferrer"
                       className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-3 rounded-full shadow-lg shadow-blue-200">
                        Start creating today →
                    </a>
                    <div className="flex gap-5 text-sm text-slate-400">
                        <a href="https://github.com/GMISWE" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600">GitHub</a>
                        <a href="https://x.com/gmi_cloud" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600">X</a>
                        <a href="https://discord.gg/gmicloud" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600">Discord</a>
                    </div>
                    <div className="text-xs text-slate-400">
                        Based on <a href="https://github.com/Muaviatanveer/Scam-Sense" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-600">Scam-Sense</a> by Muavia Tanveer
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default ScamSenseApp;